<template>
  <div class="project-check-settings">
    <!-- Page Header -->
    <div class="settings-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-cog"></i>
        </div>
        <div class="header-text">
          <h3 class="header-title">Comprehensive Check Settings</h3>
          <p class="header-subtitle">
            Configure which comprehensive check definition should be used when documents are uploaded to this project.
          </p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="loading-text">Loading comprehensive check settings...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-card">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="error-content">
          <h5 class="error-title">Error Loading Settings</h5>
          <p class="error-message">{{ error }}</p>
          <button @click="loadData" class="btn btn-outline-danger btn-sm retry-btn">
            <i class="fas fa-redo me-2"></i>Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="settings-content">
      <!-- Project Information & Current Status -->
      <div class="info-grid">
        <!-- Project Info Card -->
        <div class="info-card project-info">
          <div class="card-icon bg-primary">
            <i class="fas fa-folder-open"></i>
          </div>
          <div class="card-content">
            <h5 class="card-title">Project Information</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Name:</span>
                <span class="info-value">{{ projectData.project_name }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Type:</span>
                <span class="info-value">{{ projectData.project_type }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Available Checks:</span>
                <span class="info-badge">{{ availableChecks.length }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Check Status Card -->
        <div class="info-card status-info">
          <div class="card-icon" :class="currentCheck ? 'bg-success' : 'bg-warning'">
            <i :class="currentCheck ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
          </div>
          <div class="card-content">
            <h5 class="card-title">Current Configuration</h5>
            <div v-if="currentCheck" class="status-configured">
              <div class="current-check-name">{{ currentCheck.name }}</div>
              <div class="current-check-description">
                {{ currentCheck.description || 'No description available' }}
              </div>
              <div class="status-badges-row">
                <div class="status-badge success">
                  <i class="fas fa-check me-1"></i>Configured
                </div>
                <router-link 
                  :to="`/projects/${projectId}/lab`" 
                  class="lab-link"
                >
                  <i class="fas fa-flask"></i> Open Lab
                </router-link>
              </div>
            </div>
            <div v-else class="status-automatic">
              <div class="automatic-title">Automatic Selection</div>
              <div class="automatic-description">
                Using default selection based on project type
              </div>
              <div class="status-badge warning">
                <i class="fas fa-magic me-1"></i>Auto Mode
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Configuration Form -->
      <div class="config-section">
        <div class="section-header">
          <h4 class="section-title">
            <i class="fas fa-sliders-h me-2"></i>
            Configuration
          </h4>
          <div class="section-divider"></div>
        </div>

        <form @submit.prevent="updateProjectSettings" class="config-form">
          <div class="form-section">
            <label for="checkSelection" class="form-label">
              <i class="fas fa-list-check me-2"></i>
              Select Comprehensive Check Definition
            </label>
            
            <select 
              id="checkSelection" 
              v-model="selectedCheckId" 
              class="form-select custom-select"
              :disabled="saving"
            >
                              <option :value="null" class="auto-option">
                  🪄 Use Automatic Selection (based on project type)
                </option>
              <option 
                v-for="check in availableChecks" 
                :key="check.id" 
                :value="check.id"
                class="check-option"
              >
                {{ check.name }}
                <span v-if="check.requirements && check.requirements.length">
                  ({{ check.requirements.length }} requirements)
                </span>
              </option>
            </select>
            
            <div class="form-help">
              Choose a specific check definition or leave on automatic to use the default selection based on project type.
            </div>
          </div>

          <!-- LLM Provider Selection -->
          <div class="form-section">
            <label for="llmProviderSelection" class="form-label">
              <i class="fas fa-robot me-2"></i>
              LLM Provider for Analysis
            </label>
            
            <select 
              id="llmProviderSelection" 
              v-model="selectedLlmProvider" 
              class="form-select custom-select"
              :disabled="saving"
            >
              <option :value="null" class="auto-option">
                🤖 Use Organization Default
              </option>
              <option value="openai" class="provider-option">
                🧠 OpenAI GPT-4 (Premium Accuracy)
              </option>
              <option value="claude" class="provider-option">
                ⚡ Claude 3 Haiku (Cost Optimized - Validated!)
              </option>
              <option value="grok" class="provider-option">
                🚀 Grok (xAI)
              </option>
              <option value="gemini" class="provider-option">
                💎 Gemini (Google)
              </option>
            </select>
            
            <div class="form-help">
              Choose an LLM provider for this project's analyses. Different providers offer different cost/accuracy trade-offs.
              <br><small class="text-success">✅ Migration completed - Settings will now persist!</small>
            </div>
          </div>

          <!-- LLM Model Variant Selection -->
          <div class="form-section" v-if="selectedLlmProvider">
            <label for="llmModelSelection" class="form-label">
              <i class="fas fa-cog me-2"></i>
              Model Variant for {{ getProviderDisplayName(selectedLlmProvider) }}
            </label>
            
            <select 
              id="llmModelSelection" 
              v-model="selectedLlmModel" 
              class="form-select custom-select"
              :disabled="saving"
            >
              <option :value="null" class="auto-option">
                🤖 Use Provider Default
              </option>
              <option 
                v-for="model in getAvailableModels(selectedLlmProvider)" 
                :key="model.value" 
                :value="model.value" 
                class="model-option"
              >
                {{ model.icon }} {{ model.label }}
              </option>
            </select>
            
            <div class="form-help">
              Choose a specific model variant for more precise control over cost and performance.
            </div>
          </div>

          <!-- Selected Check Preview -->
          <div v-if="selectedCheckId && selectedCheckDetails" class="preview-section">
            <div class="preview-card">
              <div class="preview-header">
                <i class="fas fa-eye me-2"></i>
                <span>Selected Check Preview</span>
              </div>
              <div class="preview-content">
                <div class="preview-row">
                  <span class="preview-label">Name:</span>
                  <span class="preview-value">{{ selectedCheckDetails.name }}</span>
                </div>
                <div v-if="selectedCheckDetails.description" class="preview-row">
                  <span class="preview-label">Description:</span>
                  <span class="preview-value">{{ selectedCheckDetails.description }}</span>
                </div>
                <div v-if="selectedCheckDetails.requirements" class="preview-row">
                  <span class="preview-label">Requirements:</span>
                  <span class="preview-value">
                    {{ selectedCheckDetails.requirements.length }} check{{ selectedCheckDetails.requirements.length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="selectedCheckDetails.document_types" class="preview-row">
                  <span class="preview-label">Document Types:</span>
                  <div class="preview-badges">
                    <span 
                      v-for="docType in selectedCheckDetails.document_types" 
                      :key="docType" 
                      class="doc-type-badge"
                    >
                      {{ docType }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="actions-section">
            <button 
              type="submit" 
              class="btn btn-primary btn-save"
              :disabled="saving || !hasChanges"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i v-else class="fas fa-save me-2"></i>
              {{ saving ? 'Saving Changes...' : 'Save Changes' }}
            </button>
            
            <button 
              type="button" 
              @click="resetForm"
              class="btn btn-outline-secondary btn-reset"
              :disabled="saving || !hasChanges"
            >
              <i class="fas fa-undo me-2"></i>
              Reset Changes
            </button>
          </div>
        </form>
      </div>

      <!-- Project Archive Section -->
      <div class="config-section archive-section">
        <div class="section-header">
          <h4 class="section-title">
            <i class="fas fa-archive me-2"></i>
            Project Archive
          </h4>
          <div class="section-divider"></div>
        </div>

        <div class="archive-content">
          <div class="archive-info">
            <div class="archive-status">
              <span v-if="projectData.archived" class="status-badge archived">
                <i class="fas fa-archive me-1"></i>Archived
              </span>
              <span v-else class="status-badge active">
                <i class="fas fa-folder-open me-1"></i>Active
              </span>
            </div>
            <div class="archive-description">
              <p v-if="projectData.archived" class="text-muted">
                This project is currently archived and hidden from the main projects list. 
                You can unarchive it to make it active again.
              </p>
              <p v-else class="text-muted">
                Archiving this project will hide it from the main projects list but preserve all data. 
                You can view archived projects by toggling the archived filter on the projects page.
              </p>
            </div>
          </div>

          <div class="archive-actions">
            <button 
              v-if="projectData.archived"
              @click="unarchiveProject"
              :disabled="archiveLoading"
              class="btn btn-success archive-btn"
            >
              <i v-if="archiveLoading" class="fas fa-spinner fa-spin me-2"></i>
              <i v-else class="fas fa-folder-open me-2"></i>
              {{ archiveLoading ? 'Unarchiving...' : 'Unarchive Project' }}
            </button>
            <button 
              v-else
              @click="archiveProject"
              :disabled="archiveLoading"
              class="btn btn-warning archive-btn"
            >
              <i v-if="archiveLoading" class="fas fa-spinner fa-spin me-2"></i>
              <i v-else class="fas fa-archive me-2"></i>
              {{ archiveLoading ? 'Archiving...' : 'Archive Project' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="success-message">
        <div class="success-card">
          <div class="success-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="success-content">
            <h6 class="success-title">Settings Updated</h6>
            <p class="success-text">{{ successMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'ProjectCheckSettings',
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      archiveLoading: false,
      error: null,
      successMessage: null,
      
      // Project and check data
      projectData: {},
      availableChecks: [],
      currentCheck: null,
      
      // Form state
      selectedCheckId: null,
      originalCheckId: null,
      selectedLlmProvider: null,
      originalLlmProvider: null,
      selectedLlmModel: null,
      originalLlmModel: null
    }
  },
  computed: {
    selectedCheckDetails() {
      if (!this.selectedCheckId) return null
      return this.availableChecks.find(check => check.id === this.selectedCheckId)
    },
    hasChanges() {
      return this.selectedCheckId !== this.originalCheckId || 
             this.selectedLlmProvider !== this.originalLlmProvider ||
             this.selectedLlmModel !== this.originalLlmModel
    }
  },
  watch: {
    // Emit when user changes the dropdown selection (for live preview in customizer)
    selectedCheckId(newId) {
      if (newId) {
        const check = this.availableChecks.find(c => c.id === newId)
        if (check) {
          this.$emit('settings-updated', {
            projectId: this.projectId,
            selectedCheck: check,
            selectedLlmProvider: this.selectedLlmProvider
          })
        }
      } else {
        // Emit null to hide customizer
        this.$emit('settings-updated', {
          projectId: this.projectId,
          selectedCheck: null,
          selectedLlmProvider: this.selectedLlmProvider
        })
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.get(`/projects/${this.projectId}/available-comprehensive-checks/`)
        
        this.projectData = {
          project_name: response.data.project_name,
          project_type: response.data.project_type,
          archived: response.data.project_archived || false
        }
        
        this.availableChecks = response.data.available_checks || []
        this.currentCheck = response.data.current_selected_check
        
        // Set form state for both check definition and LLM provider
        this.selectedCheckId = this.currentCheck?.id || null
        this.originalCheckId = this.selectedCheckId
        
        this.selectedLlmProvider = response.data.current_llm_provider || null
        this.originalLlmProvider = this.selectedLlmProvider
        
        this.selectedLlmModel = response.data.current_llm_model || null
        this.originalLlmModel = this.selectedLlmModel
        
        // Emit initial check to parent on load (for ClientRequirementsCustomizer)
        if (this.currentCheck) {
          this.$emit('settings-updated', {
            projectId: this.projectId,
            selectedCheck: this.currentCheck,
            selectedLlmProvider: this.selectedLlmProvider
          })
        }
        
      } catch (error) {
        console.error('Error loading comprehensive check data:', error)
        this.error = error.response?.data?.error || 'Failed to load comprehensive check settings'
      } finally {
        this.loading = false
      }
    },
    
    async updateProjectSettings() {
      this.saving = true
      this.error = null
      this.successMessage = null
      
      try {
        // Update both comprehensive check and LLM provider
        const updateData = {
          check_definition_id: this.selectedCheckId,
          default_llm_provider: this.selectedLlmProvider,
          default_llm_model: this.selectedLlmModel
        }
        
        const response = await api.post(`/projects/${this.projectId}/update-project-settings/`, updateData)
        
        if (response.data.success) {
          this.successMessage = response.data.message
          this.originalCheckId = this.selectedCheckId
          this.originalLlmProvider = this.selectedLlmProvider
          this.originalLlmModel = this.selectedLlmModel
          this.currentCheck = response.data.selected_check
          
          // Emit event to parent component
          this.$emit('settings-updated', {
            projectId: this.projectId,
            selectedCheck: response.data.selected_check,
            selectedLlmProvider: this.selectedLlmProvider
          })
          
          // Clear success message after 4 seconds
          setTimeout(() => {
            this.successMessage = null
          }, 4000)
        }
        
      } catch (error) {
        console.error('Error updating project settings:', error)
        this.error = error.response?.data?.error || 'Failed to update project settings'
      } finally {
        this.saving = false
      }
    },
    
    resetForm() {
      this.selectedCheckId = this.originalCheckId
      this.selectedLlmProvider = this.originalLlmProvider
      this.selectedLlmModel = this.originalLlmModel
      this.error = null
      this.successMessage = null
    },

    getProviderDisplayName(provider) {
      const names = {
        'openai': 'OpenAI',
        'claude': 'Claude',
        'grok': 'Grok',
        'gemini': 'Gemini'
      }
      return names[provider] || provider
    },

    getAvailableModels(provider) {
      const modelOptions = {
        'openai': [
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Best reasoning)', icon: '🤖' },
          { value: 'gpt-4o', label: 'GPT-4o (Latest, multimodal)', icon: '🔥' },
          { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Fast & cheap)', icon: '⚡' }
        ],
        'claude': [
          { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet (Latest, best)', icon: '🧠' },
          { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet (Balanced)', icon: '🎯' },
          { value: 'claude-3-haiku', label: 'Claude 3 Haiku (Fast & cheap)', icon: '💨' }
        ],
        'grok': [
          { value: 'grok-2-1212', label: 'Grok 2 (X.AI)', icon: '⚡' }
        ],
        'gemini': [
          { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Latest, best)', icon: '💎' },
          { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Fast & cheap)', icon: '⚡' },
          { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (UNLIMITED daily)', icon: '🚀' },
          { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro (High limits, quality)', icon: '🔷' },
          { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (High limits, fast)', icon: '💨' }
        ]
      }
      return modelOptions[provider] || []
    },

    async archiveProject() {
      this.archiveLoading = true
      this.error = null
      this.successMessage = null
      
      try {
        await api.post(`/projects/${this.projectId}/archive/`)
        this.projectData.archived = true
        this.successMessage = 'Project archived successfully'
        
        // Emit event to parent component if needed
        this.$emit('project-archived', this.projectId)
        
      } catch (error) {
        console.error('Error archiving project:', error)
        this.error = error.response?.data?.error || 'Failed to archive project'
      } finally {
        this.archiveLoading = false
      }
    },

    async unarchiveProject() {
      this.archiveLoading = true
      this.error = null
      this.successMessage = null
      
      try {
        await api.post(`/projects/${this.projectId}/unarchive/`)
        this.projectData.archived = false
        this.successMessage = 'Project unarchived successfully'
        
        // Emit event to parent component if needed
        this.$emit('project-unarchived', this.projectId)
        
      } catch (error) {
        console.error('Error unarchiving project:', error)
        this.error = error.response?.data?.error || 'Failed to unarchive project'
      } finally {
        this.archiveLoading = false
      }
    }
  }
}
</script>

<style scoped>
.project-check-settings {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Header Styling */
.settings-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  backdrop-filter: blur(10px);
}

.header-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
}

.header-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1rem;
  line-height: 1.5;
}

/* Loading States */
.loading-container, .error-container {
  padding: 3rem 0;
  text-align: center;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-text {
  color: #6c757d;
  margin: 0;
}

.error-card {
  background: #fff;
  border: 1px solid #f5c2c7;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.1);
}

.error-icon {
  color: #dc3545;
  font-size: 2rem;
}

.error-title {
  margin: 0 0 0.5rem 0;
  color: #dc3545;
}

.error-message {
  margin: 0 0 1rem 0;
  color: #721c24;
}

.retry-btn {
  border-radius: 8px;
  padding: 0.5rem 1rem;
}

/* Main Content */
.settings-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}

.info-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.card-icon.bg-primary {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.card-icon.bg-success {
  background: linear-gradient(135deg, #28a745, #1e7e34);
}

.card-icon.bg-warning {
  background: linear-gradient(135deg, #ffc107, #e0a800);
}

.card-content {
  flex: 1;
}

.card-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-weight: 500;
  color: #6c757d;
}

.info-value {
  font-weight: 600;
  color: #2c3e50;
}

.info-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Status Specific Styling */
.current-check-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.current-check-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.automatic-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.automatic-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
}

.status-badge.warning {
  background: #fff3cd;
  color: #856404;
}

.status-badges-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.lab-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: #667eea;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid #667eea;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.lab-link:hover {
  background: #667eea;
  color: white;
}

/* Configuration Section */
.config-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.section-header {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-divider {
  height: 2px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 1px;
  opacity: 0.3;
}

.config-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.custom-select {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: #fff;
}

.custom-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  outline: none;
}

.custom-select:disabled {
  background-color: #f8f9fa;
  opacity: 0.8;
}

.form-help {
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.4;
}

/* Preview Section */
.preview-section {
  margin-top: 1rem;
}

.preview-card {
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
  border: 1px solid #bbdefb;
  border-radius: 12px;
  overflow: hidden;
}

.preview-header {
  background: rgba(102, 126, 234, 0.1);
  padding: 1rem;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #bbdefb;
  display: flex;
  align-items: center;
}

.preview-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.preview-label {
  font-weight: 600;
  color: #5a6c7d;
  min-width: 120px;
  flex-shrink: 0;
}

.preview-value {
  color: #2c3e50;
  line-height: 1.4;
}

.preview-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.doc-type-badge {
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Action Buttons */
.actions-section {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn-save {
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  border-radius: 10px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(40, 167, 69, 0.4);
}

.btn-save:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

.btn-reset {
  border: 2px solid #6c757d;
  border-radius: 10px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-reset:hover:not(:disabled) {
  background-color: #6c757d;
  color: white;
  transform: translateY(-1px);
}

/* Archive Section Styling */
.archive-section {
  margin-top: 2rem;
}

.archive-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.archive-info {
  flex: 1;
}

.archive-status {
  margin-bottom: 1rem;
}

.status-badge.archived {
  background: #fed7d7;
  color: #9b2c2c;
  border: 1px solid #feb2b2;
}

.status-badge.active {
  background: #c6f6d5;
  color: #276749;
  border: 1px solid #9ae6b4;
}

.archive-description p {
  margin: 0;
  line-height: 1.5;
}

.archive-actions {
  flex-shrink: 0;
}

.archive-btn {
  min-width: 160px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.archive-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.archive-btn:disabled {
  transform: none;
  box-shadow: none;
}

/* Success Message */
.success-message {
  margin-top: 1rem;
}

.success-card {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  border: 1px solid #c3e6cb;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.1);
}

.success-icon {
  color: #155724;
  font-size: 1.5rem;
}

.success-title {
  margin: 0 0 0.25rem 0;
  color: #155724;
  font-weight: 600;
}

.success-text {
  margin: 0;
  color: #155724;
}

/* Responsive Design */
@media (max-width: 768px) {
  .project-check-settings {
    padding: 0 10px;
  }
  
  .settings-header {
    padding: 1.5rem;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .header-title {
    font-size: 1.5rem;
  }
  
  .config-section {
    padding: 1.5rem;
  }
  
  .actions-section {
    flex-direction: column;
  }
  
  .preview-row {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .preview-label {
    min-width: auto;
  }
}

/* Animation for smooth transitions */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.settings-content {
  animation: fadeIn 0.5s ease-out;
}
</style> 